import { useEffect } from 'react';
import styled from '@emotion/styled';
import { useLogout } from 'features/auth';
import { useLogout as useLogoutFederated } from 'features/auth/hooks/federatedAuthHooks';
import { useUser } from 'features/auth/';
import { useGet } from 'lib/api';
import type { LoggedInUser } from 'features/accessControl/types';
import { UserAvatar } from './UserAvatar';
import { Copy } from '@phosphor-icons/react';
import { IconButton } from 'components';
import { t } from '@lingui/macro';
import { useTheme } from '@emotion/react';
import { useNotification } from 'features/notifications';
import { Tooltip } from 'components/tooltip';
import { useDialogs } from './useDialogs';
import { USE_FEDERATED_AUTH } from 'config';
import { apiUsers } from 'api/users';

export const UserDetails = () => {
  const { data: user } = useUser();

  const theme = useTheme();
  const { notify } = useNotification();
  const logout = useLogout({
    onSuccess: () => {
      window.location.replace('/');
    },
  });
  const logoutFederated = useLogoutFederated();
  const { inviteCodeDialog } = useDialogs();
  const { data: CurrentUser } = useGet<LoggedInUser>(apiUsers.get.loggedInUser, 'logged-in-user');

  const handleCopyInviteCode = async () => {
    if (user?.loginContext?.inviteCode && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(user.loginContext.inviteCode);
      notify.info({
        message: t`Copied to clipboard!`,
        id: 'invite-code-copy',
        noDuplicate: true,
      });
    }
  };

  useEffect(() => {
    if (user?.loginContext?.inviteCode) {
      inviteCodeDialog(user?.loginContext?.inviteCode);
    }
  }, [user?.loginContext?.inviteCode]);

  return (
    <Container>
      {user?.loginContext?.inviteCode && (
        <InviteCodeContainer>
          <InviteCodeText>{user?.loginContext?.inviteCode}</InviteCodeText>
          <Tooltip title={t`Copy invite code`}>
            <div>
              <IconButton data-testid="copy-invite" size="lg" onClick={handleCopyInviteCode}>
                <Copy size={18} color={theme.palette.border.accentPrimary} />
              </IconButton>
            </div>
          </Tooltip>
        </InviteCodeContainer>
      )}
      <Greeting>
        <span style={{ whiteSpace: 'nowrap' }}>Good to see you, {user?.loginContext?.name}</span>
        <span role="img" aria-label="partying">
          &#129395;
        </span>
      </Greeting>
      <UserAvatar
        onLogout={USE_FEDERATED_AUTH ? logoutFederated : logout}
        letters={user?.loginContext?.name}
        color={CurrentUser?.AvatarColor}
      />
    </Container>
  );
};

const Greeting = styled.span`
  color: ${p => p.theme.palette.utility.black};
  align-items: center;
  display: flex;
  gap: 0.25rem;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  align-items: center;
  span[role='img'] {
    font-size: 1.2rem;
  }
`;
const InviteCodeContainer = styled.div`
  border-radius: 4px;
  padding-left: 8px;
  padding-right: 4px;
  background-color: ${p => p.theme.palette.utility.marineBlue[100]};
  display: flex;
  align-items: center;
  white-space: nowrap;
  button {
    :hover {
      color: inherit;
      background-color: inherit;
    }
  }
`;
const InviteCodeText = styled.span`
  padding: 4px 0;
  font-size: 12px;
  color: ${p => p.theme.palette.text.link};
`;
